import React from 'react'

const ALLOWED_TAGS = ['span', 'i', 'b', 'strong', 'em', 'u', 'br'] as const
type AllowedTag = (typeof ALLOWED_TAGS)[number]

function isAllowedTag(tagName: string): tagName is AllowedTag {
  return (ALLOWED_TAGS as readonly string[]).includes(tagName)
}

/**
 * Recursively converts a parsed DOM node into a React node.
 * Only allow-listed tags are recreated as elements (with no attributes
 * copied over, so no `onclick`/`onerror`/etc. can survive). Any other
 * element is dropped but its text content is kept, mirroring the previous
 * `KEEP_CONTENT: true` sanitizer behavior. Text nodes pass through as plain
 * strings, which React escapes automatically.
 */
function nodeToReact(node: ChildNode, key: number): React.ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null
  }

  const element = node as Element
  const tagName = element.tagName.toLowerCase()
  const children = Array.from(element.childNodes).map((child, index) => nodeToReact(child, index))

  if (!isAllowedTag(tagName)) {
    return children
  }

  if (tagName === 'br') {
    return React.createElement('br', { key })
  }

  return React.createElement(tagName, { key }, children)
}

/**
 * Parses a string containing only allow-listed formatting tags into React
 * nodes, without ever using `dangerouslySetInnerHTML`. Parsing with
 * `DOMParser` never executes scripts or event handlers since the resulting
 * document is never attached to the live DOM.
 */
function parseSafeHtml(content: string): React.ReactNode {
  const doc = new DOMParser().parseFromString(`<body>${content}</body>`, 'text/html')
  return Array.from(doc.body.childNodes).map((node, index) => nodeToReact(node, index))
}

/**
 * This function is used to render a label with HTML tags.
 * It is used to render the label with HTML tags in the input wrapper.
 * Only simple formatting tags are allowed: span, i, b, strong, em, u, br.
 * Content is rebuilt as real React elements (never injected as raw HTML),
 * so there is no XSS surface and no sanitizer dependency is needed.
 * @param content - The content to render.
 * @returns The rendered content.
 */
export function renderLabel(content: string | React.ReactNode) {
  if (typeof content === 'string') {
    const htmlTagRegex = /<\s*\/?\s*(span|i|b|strong|em|u|br)\b[^>]*>/i

    if (htmlTagRegex.test(content)) {
      return <>{parseSafeHtml(content)}</>
    }
    return content
  }

  if (React.isValidElement(content)) {
    const elementType = content.type
    if (typeof elementType === 'string' && isAllowedTag(elementType)) {
      return content
    }
  }

  if (content && process.env.NODE_ENV === 'development') {
    console.warn(
      'Label should be a string or a React element with a simple formatting tag (e.g., <span>, <i>, <b>).',
    )
  }

  return null
}
